import { Radio, type RadioChangeEvent, type RadioProps } from 'antd';
import clx from 'classnames';
import type React from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useField } from '../field/useField.tsx';
import { useController } from '../hooks/useController.ts';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';

const StyledRadio = styled(Radio)`
  &.read-only {
    pointer-events: none;
  }
`;

export interface IRadioOptionInputProps extends Omit<RadioProps, 'name'> {
  name: string;
  optionValue: string | number;
  /**
   * Permette di deselezionare l'opzione premendo sull'opzione stessa.
   */
  allowUnselect?: boolean;
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   */
  readOnly?: boolean;
  /**
   * in caso che dobbiamo trasformare il valore per l'input
   */
  transformValue?: (value: any) => any;
}

export function RadioOptionInput(props: IRadioOptionInputProps) {
  const {
    name,
    optionValue,
    allowUnselect,
    readOnly,
    className,
    transformValue,
    ...inputProps
  } = props;

  const { enabled: parentEnabled, visuallyDisabled: parentVisuallyDisabled } =
    useFormItemEnabled();

  const {
    enabled: groupEnabled,
    layoutEnabled: groupLayoutEnabled,
    visible,
    unregister,
  } = useField(name);

  // Flag di abilitazione del campo
  const enabled = useMemo(
    () => parentEnabled !== false && groupEnabled !== false,
    [groupEnabled, parentEnabled],
  );

  // Flag di abilitazione UI/UX del campo
  // A diferrenza di enabled, blocca l'edit solo a livello di layout
  // ma non modifica le logiche di reset/validazione.
  // Utilizzato, ad esempio, per disabilitare un campo dopo la conferma di un ConfirmWidget.
  const layoutEnabled = useMemo(
    () => groupLayoutEnabled !== false && !parentVisuallyDisabled,
    [groupLayoutEnabled, parentVisuallyDisabled],
  );

  const isDisabled = useMemo(() => {
    // È disabilitato se almeno uno dei flag è false
    // o se è indicato dalle inputProps
    return enabled === false || layoutEnabled === false || inputProps.disabled;
  }, [enabled, inputProps.disabled, layoutEnabled]);

  const {
    field: { value: currentValue, onChange, onBlur, ref },
  } = useController(name);
  const transformedValue = transformValue?.(currentValue) ?? currentValue;

  useEffect(() => {
    if (visible) return;
    unregister();
  }, [unregister, visible]);

  // Il reset del valore su disabilitazione è gestito automaticamente
  // dallo schema Yup (setEnabledCondition → transform → getDefault)
  // tramite syncComputedValues.

  const handleChange = useCallback(
    (e: RadioChangeEvent) => {
      onChange(e.target.value);
      onBlur();
    },
    [onChange, onBlur],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLInputElement>) => {
      // Permette al campo di essere deselezionato premendo sulla stessa opzione.
      if (allowUnselect && transformedValue === optionValue) {
        onChange(null);
        onBlur();
        // Disabilitiamo l'onChange che "riattiverebbe" il campo
        e.preventDefault();
      }
    },
    [allowUnselect, optionValue, transformedValue, onChange, onBlur],
  );

  const checked = useMemo(() => {
    return optionValue === transformedValue;
  }, [optionValue, transformedValue]);

  if (!visible) return null;

  return (
    <StyledRadio
      {...inputProps}
      disabled={isDisabled ?? inputProps.disabled}
      ref={ref}
      onChange={readOnly ? undefined : handleChange}
      onClick={readOnly ? undefined : handleClick}
      value={optionValue}
      checked={checked}
      className={clx(className, { 'read-only': readOnly })}
    />
  );
}
